import React, { useState, useEffect, useCallback } from 'react';
import { Eye, EyeOff, AlertCircle, RefreshCw } from 'lucide-react';

const KAIRALI_LOGO = 'https://www.kairali.com/images/kairali-logo.png';

const ROLE_COLOR = {
  'Operation':              { bg: 'rgba(59,130,246,0.15)',  text: '#93C5FD' },
  'PC':                     { bg: 'rgba(34,197,94,0.15)',   text: '#86EFAC' },
  'Sales Agent':            { bg: 'rgba(168,85,247,0.15)',  text: '#C4B5FD' },
  'Medical Representative': { bg: 'rgba(239,68,68,0.15)',   text: '#FCA5A5' },
  'HR':                     { bg: 'rgba(234,179,8,0.15)',   text: '#FDE047' },
  'Account':                { bg: 'rgba(99,102,241,0.15)',  text: '#A5B4FC' },
};
const getRoleStyle = (role) => ROLE_COLOR[role] || { bg: 'rgba(255,255,255,0.08)', text: 'rgba(255,255,255,0.6)' };

const cssStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');

  @keyframes k-leaf-a  { 0%,100%{ transform:translateY(0) rotate(-6deg);  opacity:.06 } 50%{ transform:translateY(-18px) rotate(6deg);  opacity:.12 } }
  @keyframes k-leaf-b  { 0%,100%{ transform:translateY(0) rotate(10deg);  opacity:.04 } 50%{ transform:translateY(-14px) rotate(-8deg); opacity:.09 } }
  @keyframes k-leaf-c  { 0%,100%{ transform:translateY(0) rotate(-4deg);  opacity:.05 } 50%{ transform:translateY(-10px)  rotate(4deg);  opacity:.10 } }
  @keyframes k-fade-up { from{ opacity:0; transform:translateY(24px) } to{ opacity:1; transform:translateY(0) } }
  @keyframes k-glow    { 0%,100%{ box-shadow:0 0 40px rgba(78,189,116,.15),0 0 0 1px rgba(78,189,116,.20) } 50%{ box-shadow:0 0 70px rgba(78,189,116,.28),0 0 0 1px rgba(78,189,116,.30) } }
  @keyframes k-shimmer { 0%{ background-position:200% center } 100%{ background-position:-200% center } }
  @keyframes k-spin    { to{ transform:rotate(360deg) } }
  @keyframes k-pulse-ring { 0%{ transform:scale(1); opacity:.5 } 100%{ transform:scale(1.25); opacity:0 } }

  * { font-family: 'Plus Jakarta Sans', sans-serif; }

  .k-input-dark {
    width:100%; box-sizing:border-box;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.10);
    border-radius: 12px;
    padding: 12px 14px;
    font-size: 14px;
    color: #fff;
    letter-spacing: -0.1px;
    transition: border-color .2s, background .2s;
    outline: none;
  }
  .k-input-dark::placeholder { color: rgba(255,255,255,0.25); }
  .k-input-dark:focus {
    border-color: rgba(212,168,64,0.7) !important;
    background: rgba(255,255,255,0.06) !important;
  }

  .k-drop-item-dark:hover { background: rgba(255,255,255,0.06) !important; }

  .k-login-root {
    min-height: 100svh;
    background: #000;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    position: relative; overflow: hidden; padding: 40px 20px;
  }
  .k-login-root::before {
    content: '';
    position: absolute; inset: 0; pointer-events: none;
    background:
      radial-gradient(ellipse 70% 55% at 10% 90%, rgba(12,40,22,0.85) 0%, transparent 70%),
      radial-gradient(ellipse 50% 40% at 90% 10%, rgba(8,26,15,0.60) 0%, transparent 60%);
  }
  .k-login-root::after {
    content: '';
    position: absolute; inset: 0; pointer-events: none;
    background-image: radial-gradient(rgba(255,255,255,0.025) 1px, transparent 1px);
    background-size: 28px 28px;
  }

  /* ── Responsive component classes ── */
  .k-login-card-wrap { width: 100%; max-width: 400px; }
  .k-login-card      { padding: 24px 24px 20px; }
  .k-card-title      { font-size: 18px; font-weight: 700; color: #fff; letter-spacing: -0.4px; line-height: 1.2; margin: 0; }
  .k-brand-eyebrow   { font-size: 10px; font-weight: 600; letter-spacing: 0.22em; color: rgba(255,255,255,0.35); text-transform: uppercase; margin-bottom: 8px; }
  .k-brand-title     { font-size: 26px; font-weight: 700; line-height: 1.15; margin-bottom: 5px; letter-spacing: -0.5px;
                       background: linear-gradient(90deg,#ffffff 0%,#D4A840 40%,#ffffff 80%);
                       background-size: 200% auto; -webkit-background-clip: text; -webkit-text-fill-color: transparent;
                       animation: k-shimmer 5s linear infinite; }
  .k-brand-tagline   { font-size: 11px; color: rgba(255,255,255,0.28); letter-spacing: 0.18em; font-weight: 500; text-transform: uppercase; margin: 0; }
  .k-logo-ring-wrap  { position: relative; width: 96px; height: 96px; margin: 0 auto 20px; }
  .k-logo-ring-pulse { position: absolute; inset: -6px; border-radius: 50%; border: 1px solid rgba(78,189,116,0.3); animation: k-pulse-ring 2.5s ease-out infinite; }
  .k-logo-ring-outer { width: 96px; height: 96px; border-radius: 50%; background: rgba(255,255,255,0.04); border: 1px solid rgba(78,189,116,0.22); display: flex; align-items: center; justify-content: center; animation: k-glow 4s ease-in-out infinite; backdrop-filter: blur(8px); }
  .k-logo-inner      { width: 76px; height: 76px; border-radius: 50%; background: rgba(255,255,255,0.96); display: flex; align-items: center; justify-content: center; overflow: hidden; }
  .k-logo-img        { width: 60px; height: auto; }

  /* ── Large standard-DPI (27" 1440p / 1080p) ── */
  @media (min-width: 1920px) and (max-resolution: 1.5dppx) {
    * { -webkit-font-smoothing: auto !important; -moz-osx-font-smoothing: auto !important; }
    .k-login-card-wrap { max-width: 460px; }
    .k-login-card      { padding: 30px 30px 24px; }
    .k-card-title      { font-size: 21px; }
    .k-brand-eyebrow   { font-size: 11px; }
    .k-brand-title     { font-size: 32px; letter-spacing: -0.6px; }
    .k-brand-tagline   { font-size: 12px; }
    .k-logo-ring-wrap  { width: 112px; height: 112px; margin-bottom: 24px; }
    .k-logo-ring-outer { width: 112px; height: 112px; }
    .k-logo-inner      { width: 88px; height: 88px; }
    .k-logo-img        { width: 70px; }
    .k-input-dark      { font-size: 15px; padding: 14px 16px; font-weight: 450; letter-spacing: 0; }
    .k-login-root::after { background-size: 32px 32px; }
  }

  /* ── 4K / ultra-wide standard DPI ── */
  @media (min-width: 2560px) and (max-resolution: 1.5dppx) {
    .k-login-card-wrap { max-width: 520px; }
    .k-login-card      { padding: 36px 36px 28px; }
    .k-card-title      { font-size: 24px; }
    .k-brand-title     { font-size: 38px; }
    .k-brand-tagline   { font-size: 13px; }
    .k-logo-ring-wrap  { width: 128px; height: 128px; }
    .k-logo-ring-outer { width: 128px; height: 128px; }
    .k-logo-inner      { width: 102px; height: 102px; }
    .k-logo-img        { width: 80px; }
    .k-input-dark      { font-size: 16px; padding: 15px 18px; font-weight: 500; }
    .k-login-root::after { background-size: 36px 36px; }
  }

  /* ── Mobile ── */
  @media (max-width: 480px) {
    .k-login-root  { padding: 24px 16px; }
    .k-brand-title { font-size: 22px; }
    .k-login-card  { padding: 20px 18px 16px; }
  }
`;

const LoginPage = ({ onLogin }) => {
  const [employees, setEmployees]                 = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [searchTerm, setSearchTerm]               = useState('');
  const [selectedEmployee, setSelectedEmployee]   = useState(null);
  const [passkey, setPasskey]                     = useState('');
  const [showPassword, setShowPassword]           = useState(false);
  const [error, setError]                         = useState('');
  const [loading, setLoading]                     = useState(false);
  const [dataLoading, setDataLoading]             = useState(true);

  const fetchEmployeesFromSheet = useCallback(async () => {
    try {
      setDataLoading(true);
      setError('');
      const spreadsheetId = process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_ID_EMPLOYEES;
      const sheetName = process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_NAME_EMPLOYEES || 'Employees';
      if (!spreadsheetId) throw new Error('Employee spreadsheet configuration is missing.');
      const params = new URLSearchParams({ sheetId: spreadsheetId, sheetName, range: 'A2:Z' });
      const response = await fetch(`/api/sheets?${params}`);
      if (!response.ok) throw new Error(`Failed to fetch employee data (${response.status})`);
      const data = await response.json();
      const rows = data.values || [];
      if (rows.length === 0) throw new Error('No employee data found in the spreadsheet.');
      const employeeData = rows
        .filter(row => row.length >= 7 && row[6] === 'Active')
        .map((row, index) => ({
          id:         row[0] || `EMP${String(index + 1).padStart(3, '0')}`,
          name:       row[1] || 'Unknown',
          email:      row[2] || '',
          role:       row[4] || 'Employee',
          department: row[5] || 'General',
          status:     row[6] || 'Active',
        }));
      if (employeeData.length === 0) throw new Error('No active employees found.');
      setEmployees(employeeData);
      setFilteredEmployees(employeeData);
    } catch (err) {
      console.error('Error fetching employee data:', err);
      setError(err.message);
      setEmployees([]);
      setFilteredEmployees([]);
    } finally {
      setDataLoading(false);
    }
  }, []);

  useEffect(() => { fetchEmployeesFromSheet(); }, [fetchEmployeesFromSheet]);

  useEffect(() => {
    const q = searchTerm.toLowerCase();
    setFilteredEmployees(
      employees.filter(emp =>
        emp.name.toLowerCase().includes(q) ||
        emp.role.toLowerCase().includes(q) ||
        emp.department.toLowerCase().includes(q) ||
        emp.email.toLowerCase().includes(q)
      )
    );
  }, [searchTerm, employees]);

  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    setSearchTerm(employee.name);
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    if (!selectedEmployee) { setError('Please select an employee'); setLoading(false); return; }
    if (!passkey)          { setError('Please enter your passkey');  setLoading(false); return; }
    try {
      const response = await fetch('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employeeId: selectedEmployee.id, passkey }),
      });
      const data = await response.json();
      if (!response.ok) { setError(data.error || 'Login failed'); return; }
      onLogin(data.user);
    } catch (err) {
      setError('Unable to connect to authentication service. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshData = () => {
    fetchEmployeesFromSheet();
    setError(''); setSearchTerm(''); setSelectedEmployee(null); setPasskey('');
  };

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (dataLoading) {
    return (
      <>
        <style>{cssStyles}</style>
        <div className="k-login-root">
          <Leaves />
          <div style={{ textAlign:'center', animation:'k-fade-up .8s ease both', position:'relative', zIndex:1 }}>
            <LogoRing />
            <BrandText />
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10, marginTop:28, color:'rgba(255,255,255,0.4)' }}>
              <svg style={{ width:16, height:16, animation:'k-spin .9s linear infinite', flexShrink:0 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
              </svg>
              <span style={{ fontSize:13, letterSpacing:'-0.1px', fontWeight:400 }}>Loading employee data…</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Fatal error ──────────────────────────────────────────────────────────────
  if (error && employees.length === 0) {
    return (
      <>
        <style>{cssStyles}</style>
        <div className="k-login-root">
          <Leaves />
          <div style={{ animation:'k-fade-up .8s ease both', width:'100%', maxWidth:380, position:'relative', zIndex:1 }}>
            <div style={CARD}>
              <AlertCircle style={{ width:40, height:40, color:'#F87171', margin:'0 auto 14px', display:'block' }} />
              <h2 style={{ fontSize:16, fontWeight:700, color:'#fff', marginBottom:8, textAlign:'center', letterSpacing:'-0.3px' }}>Unable to Load Employee Data</h2>
              <p style={{ fontSize:13, color:'rgba(255,255,255,0.45)', marginBottom:20, textAlign:'center', lineHeight:1.5 }}>{error}</p>
              <button onClick={handleRefreshData} style={{ ...BTN_PRIMARY, cursor:'pointer', opacity:1 }}>
                <RefreshCw size={14} style={{ flexShrink:0 }} /> Retry
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Main login ───────────────────────────────────────────────────────────────
  return (
    <>
      <style>{cssStyles}</style>
      <div className="k-login-root">
        <Leaves />

        {/* Brand */}
        <div style={{ animation:'k-fade-up .8s ease both', textAlign:'center', marginBottom:28, position:'relative', zIndex:1 }}>
          <LogoRing />
          <BrandText />
        </div>

        {/* Card */}
        <div className="k-login-card-wrap" style={{ animation:'k-fade-up .9s ease both .12s', position:'relative', zIndex:1 }}>
          <div className="k-login-card" style={CARD}>

            {/* Card header */}
            <div style={{ marginBottom:20 }}>
              <p style={{ fontSize:11, fontWeight:700, letterSpacing:'0.14em', color:'rgba(255,255,255,0.35)', textTransform:'uppercase', marginBottom:4 }}>
                Employee Login
              </p>
              <h2 className="k-card-title">Sign in to your workspace</h2>
            </div>

            {/* Divider */}
            <div style={{ height:1, background:'rgba(255,255,255,0.07)', marginBottom:20 }} />

            <form onSubmit={handleLogin} style={{ display:'flex', flexDirection:'column', gap:14 }}>

              {/* Employee search */}
              <div style={{ position:'relative' }}>
                <label style={LABEL}>Employee</label>
                <div style={{ position:'relative' }}>
                  <svg style={{ position:'absolute', left:13, top:'50%', transform:'translateY(-50%)', width:14, height:14, color:'rgba(255,255,255,0.3)', pointerEvents:'none' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                  </svg>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={e => { setSearchTerm(e.target.value); setSelectedEmployee(null); }}
                    placeholder="Search by name, role, department…"
                    className="k-input-dark"
                    style={{ paddingLeft:38 }}
                  />
                </div>

                {searchTerm && !selectedEmployee && filteredEmployees.length > 0 && (
                  <div style={DROPDOWN}>
                    {filteredEmployees.map(emp => {
                      const rc = getRoleStyle(emp.role);
                      return (
                        <div key={emp.id} onClick={() => handleEmployeeSelect(emp)} className="k-drop-item-dark"
                          style={{ padding:'10px 14px', cursor:'pointer', borderBottom:'1px solid rgba(255,255,255,0.06)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, borderRadius:0 }}>
                          <div style={{ minWidth:0 }}>
                            <p style={{ fontSize:13, fontWeight:600, color:'#fff', marginBottom:1, letterSpacing:'-0.2px' }}>{emp.name}</p>
                            <p style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>{emp.department}</p>
                          </div>
                          <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:20, background:rc.bg, color:rc.text, flexShrink:0, letterSpacing:'0.02em' }}>{emp.role}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {searchTerm && !selectedEmployee && filteredEmployees.length === 0 && (
                  <div style={{ ...DROPDOWN, padding:'14px', textAlign:'center' }}>
                    <p style={{ fontSize:13, color:'rgba(255,255,255,0.35)' }}>No employees found</p>
                  </div>
                )}
              </div>

              {/* Selected chip */}
              {selectedEmployee && (() => {
                const rc = getRoleStyle(selectedEmployee.role);
                const initials = selectedEmployee.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
                return (
                  <div style={{ padding:'10px 14px', background:'rgba(78,189,116,0.08)', borderRadius:12, border:'1px solid rgba(78,189,116,0.18)', display:'flex', alignItems:'center', gap:12 }}>
                    <div style={{ width:34, height:34, borderRadius:'50%', background:'linear-gradient(135deg,#D4A840,#E2BC56)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <span style={{ color:'#1a1a1a', fontWeight:800, fontSize:13 }}>{initials}</span>
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <p style={{ fontSize:13, fontWeight:600, color:'#fff', letterSpacing:'-0.2px' }}>{selectedEmployee.name}</p>
                      <p style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>{selectedEmployee.department}</p>
                    </div>
                    <span style={{ fontSize:10, fontWeight:700, padding:'3px 8px', borderRadius:20, background:rc.bg, color:rc.text, flexShrink:0 }}>{selectedEmployee.role}</span>
                  </div>
                );
              })()}

              {/* Passkey */}
              <div>
                <label style={LABEL}>Passkey</label>
                <div style={{ position:'relative' }}>
                  <svg style={{ position:'absolute', left:13, top:'50%', transform:'translateY(-50%)', width:14, height:14, color:'rgba(255,255,255,0.3)', pointerEvents:'none' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={passkey}
                    onChange={e => setPasskey(e.target.value)}
                    placeholder="Enter your passkey"
                    className="k-input-dark"
                    style={{ paddingLeft:38, paddingRight:44 }}
                  />
                  <button type="button" onClick={() => setShowPassword(v => !v)}
                    style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'rgba(255,255,255,0.35)', padding:0, display:'flex' }}>
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 12px', background:'rgba(239,68,68,0.10)', borderRadius:10, border:'1px solid rgba(239,68,68,0.25)' }}>
                  <AlertCircle size={14} style={{ color:'#F87171', flexShrink:0 }} />
                  <span style={{ fontSize:12, color:'#F87171', fontWeight:600 }}>{error}</span>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading || !selectedEmployee || !passkey}
                style={{
                  ...BTN_PRIMARY,
                  opacity: (loading || !selectedEmployee || !passkey) ? 0.45 : 1,
                  cursor:  (loading || !selectedEmployee || !passkey) ? 'not-allowed' : 'pointer',
                  marginTop: 2,
                }}
              >
                {loading ? (
                  <>
                    <svg style={{ width:14, height:14, animation:'k-spin .8s linear infinite', flexShrink:0 }} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2.5}>
                      <path strokeLinecap="round" d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                    </svg>
                    Signing in…
                  </>
                ) : 'Sign In →'}
              </button>
            </form>

            {/* Footer */}
            <p style={{ fontSize:10, color:'rgba(255,255,255,0.2)', textAlign:'center', marginTop:18, letterSpacing:'0.04em' }}>
              {employees.length} active employee{employees.length !== 1 ? 's' : ''} · Kairali Internal Use Only
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

function LogoRing() {
  return (
    <div className="k-logo-ring-wrap">
      <div className="k-logo-ring-pulse" />
      <div className="k-logo-ring-outer">
        <div className="k-logo-inner">
          <img
            src={KAIRALI_LOGO}
            alt="Kairali"
            className="k-logo-img"
            onError={e => {
              e.target.style.display = 'none';
              e.target.parentNode.style.background = 'linear-gradient(135deg, #2D6A46, #C8922A)';
            }}
          />
        </div>
      </div>
    </div>
  );
}

function BrandText() {
  return (
    <>
      <p className="k-brand-eyebrow">Kairali Ayurvedic Group</p>
      <h1 className="k-brand-title">Kairali Task Dashboard</h1>
      <p className="k-brand-tagline">Purely Ayurvedic</p>
    </>
  );
}

function Leaves() {
  const TWIN1 = 'https://www.kairali.com/pic/twin-leaf.png';
  const TWIN2 = 'https://www.kairali.com/pic/twin-leaf-second.png';
  return (
    <>
      <img src={TWIN1} alt="" aria-hidden="true" style={{ position:'absolute', top:'3%',    right:'4%',  width:160, opacity:.07, animation:'k-leaf-a 6s ease-in-out infinite',       pointerEvents:'none', zIndex:0, filter:'brightness(2)' }} />
      <img src={TWIN2} alt="" aria-hidden="true" style={{ position:'absolute', bottom:'8%', left:'3%',   width:120, opacity:.06, animation:'k-leaf-b 7.5s ease-in-out infinite 1.5s', pointerEvents:'none', zIndex:0, filter:'brightness(2)' }} />
      <img src={TWIN1} alt="" aria-hidden="true" style={{ position:'absolute', top:'45%',   left:'1%',   width:72,  opacity:.05, animation:'k-leaf-c 6.5s ease-in-out infinite 3s',   pointerEvents:'none', zIndex:0, filter:'brightness(2)' }} />
      <img src={TWIN2} alt="" aria-hidden="true" style={{ position:'absolute', top:'16%',   left:'6%',   width:50,  opacity:.04, animation:'k-leaf-b 9s ease-in-out infinite .8s',    pointerEvents:'none', zIndex:0, filter:'brightness(2)' }} />
      <img src={TWIN1} alt="" aria-hidden="true" style={{ position:'absolute', bottom:'26%',right:'3%',  width:68,  opacity:.05, animation:'k-leaf-a 7s ease-in-out infinite 2.2s',   pointerEvents:'none', zIndex:0, filter:'brightness(2)' }} />
    </>
  );
}

const CARD = {
  width:'100%',
  background:'rgba(10,18,13,0.92)',
  borderRadius:20,
  border:'1px solid rgba(255,255,255,0.09)',
  boxShadow:'0 32px 80px rgba(0,0,0,0.70), 0 0 0 1px rgba(255,255,255,0.04)',
  backdropFilter:'blur(20px)',
  WebkitBackdropFilter:'blur(20px)',
};

const DROPDOWN = {
  position:'absolute', zIndex:50, width:'100%', marginTop:4,
  background:'rgba(12,22,16,0.97)',
  border:'1px solid rgba(255,255,255,0.10)',
  borderRadius:14,
  boxShadow:'0 16px 48px rgba(0,0,0,0.60)',
  maxHeight:210, overflowY:'auto',
  backdropFilter:'blur(16px)',
};

const LABEL = {
  display:'block', fontSize:11, fontWeight:600, color:'rgba(255,255,255,0.4)',
  marginBottom:7, textTransform:'uppercase', letterSpacing:'0.10em',
};

const BTN_PRIMARY = {
  width:'100%', padding:'13px 0',
  background:'linear-gradient(135deg, #D4A840 0%, #B8922A 100%)',
  color:'#1a1a1a', fontWeight:800, fontSize:13, borderRadius:12, border:'none',
  letterSpacing:'0.02em', display:'flex', alignItems:'center', justifyContent:'center', gap:8,
  transition:'opacity .2s, transform .15s',
  boxShadow:'0 4px 20px rgba(212,168,64,0.35)',
};

export default LoginPage;
